import { v4 as uuidv4 } from "uuid";
import { firebaseAuth } from "@/shared/lib/firebase";
import { env } from "@/shared/lib/env";
import { crashReporting } from "@/shared/lib/crash-reporting";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Orval fetch-mutator signature. The generated client calls this as
 * `httpClient<T>(url, { method, headers, body, signal, ... })`, so our
 * signature must accept `(url, RequestInit)` exactly.
 *
 * Header casing mirrors `wanderfit-app/src/lib/api-client.ts` so Cloud
 * Logging queries (`correlationId="<id>"`) return a unified cross-client
 * trace. HTTP headers are case-insensitive on the wire.
 */

interface ResolvedUrl {
  readonly fullUrl: string;
  /**
   * Whether the resolved URL targets our trusted API origin. The Firebase
   * Bearer token is ONLY attached to requests whose final host matches the
   * configured `VITE_API_BASE_URL` origin. Any other host (CDN, third party,
   * attacker-controlled callback) gets the request without an authorization
   * header, preventing accidental token exfiltration.
   */
  readonly isTrustedOrigin: boolean;
}

function resolveUrl(url: string): ResolvedUrl {
  const base = env.VITE_API_BASE_URL.replace(/\/$/, "");
  const trustedOrigin = new URL(base).origin;

  if (/^https?:\/\//i.test(url)) {
    const parsed = new URL(url);
    return {
      fullUrl: parsed.toString(),
      isTrustedOrigin: parsed.origin === trustedOrigin,
    };
  }

  const path = url.startsWith("/") ? url : `/${url}`;
  return { fullUrl: `${base}${path}`, isTrustedOrigin: true };
}

/**
 * Normalize any HeadersInit shape (record, array, `Headers` object) into a
 * flat lowercase map. HTTP header names are case-insensitive on the wire,
 * so lowercasing lets us unambiguously look up "content-type" regardless
 * of how the caller spelled it, and lets framework headers override cleanly.
 */
function normalizeHeaders(input: HeadersInit | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!input) return out;
  new Headers(input).forEach((value, key) => {
    out[key.toLowerCase()] = value;
  });
  return out;
}

async function getAuthHeader(
  isTrustedOrigin: boolean,
  forceRefresh = false,
): Promise<Record<string, string>> {
  // Hard rule: never attach the Bearer token to cross-origin requests.
  if (!isTrustedOrigin) return {};
  const user = firebaseAuth.currentUser;
  if (!user) return {};
  try {
    // `getIdToken(false)` returns the cached token if it has >5 min of life;
    // `getIdToken(true)` always hits the refresh endpoint. The 401 retry path
    // calls with `true` to bypass cases where the SDK believes the cached
    // token is still valid but the API has already rejected it (clock skew,
    // backgrounded tabs that missed a refresh tick, etc.).
    const token = await user.getIdToken(forceRefresh);
    return { authorization: `Bearer ${token}` };
  } catch (err) {
    throw new Error("Failed to retrieve Firebase ID token for request", { cause: err });
  }
}

/**
 * A request body is "replayable" if we can send the same `RequestInit` to
 * `fetch` twice and get the same payload on the wire. Everything in the
 * `BodyInit` union is replayable except `ReadableStream`, which is consumed
 * on first send. Skipping the 401 retry for streamed bodies avoids sending
 * an empty body on the second attempt.
 */
function isReplayableBody(body: BodyInit | null | undefined): boolean {
  if (body == null) return true;
  if (typeof body === "string") return true;
  if (body instanceof FormData) return true;
  if (body instanceof Blob) return true;
  if (body instanceof URLSearchParams) return true;
  if (body instanceof ArrayBuffer) return true;
  if (ArrayBuffer.isView(body)) return true;
  return false;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  // 204 No Content and 205 Reset Content never have a body.
  if (response.status === 204 || response.status === 205) return null;

  const contentType = response.headers.get("content-type") ?? "";
  const contentLength = response.headers.get("content-length");
  if (contentLength === "0") return null;

  if (!contentType.includes("application/json")) {
    const text = await response.text();
    return text.length === 0 ? null : text;
  }

  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError("Malformed JSON in response", response.status, text);
  }
}

/**
 * The one request pipeline both `httpClient` and `fetchBlob` build on:
 * resolve the URL, attach `x-request-id` + the Bearer token (trusted origin
 * only), send, and retry ONCE with a force-refreshed token on 401. Returns
 * the raw `Response`; what to do with the body is the caller's divergence.
 *
 * 401 retry: a cached-but-stale ID token can pass Firebase's 5-min
 * freshness check yet be rejected by the API (clock skew, backgrounded
 * tabs, mid-session token revocation). Same `correlationId` on both
 * attempts so they land under one trace in Cloud Logging. Guards: only on
 * the trusted origin, only when authenticated, only if the body can be
 * replayed (ReadableStream is consumed on first send).
 */
async function fetchWithAuthRetry(
  url: string,
  init: RequestInit,
  correlationId: string,
): Promise<Response> {
  const { fullUrl, isTrustedOrigin } = resolveUrl(url);
  const method = (init.method ?? "GET").toUpperCase();
  const callerHeaders = normalizeHeaders(init.headers);

  // Framework headers — always set, always win. Authorization is added
  // last so no caller can override or strip the Firebase Bearer token,
  // and only when the target is our trusted API origin.
  //
  // We intentionally do NOT default `content-type`. The Orval-generated
  // client already sets `Content-Type: application/json` on every JSON
  // write, and `fetch` picks the correct value (with boundary) for
  // FormData/Blob/URLSearchParams/streams. Defaulting here would have
  // masked a missing header on manual text/CSV/XML call sites as a
  // silent 415 — safer to let the caller be explicit.
  const buildHeaders = async (forceRefresh: boolean): Promise<Record<string, string>> => {
    const framework: Record<string, string> = {
      "x-request-id": correlationId,
    };
    Object.assign(framework, await getAuthHeader(isTrustedOrigin, forceRefresh));
    return { ...callerHeaders, ...framework };
  };

  let response = await fetch(fullUrl, { ...init, method, headers: await buildHeaders(false) });
  if (
    response.status === 401 &&
    isTrustedOrigin &&
    firebaseAuth.currentUser &&
    isReplayableBody(init.body)
  ) {
    response = await fetch(fullUrl, { ...init, method, headers: await buildHeaders(true) });
  }
  return response;
}

/** Decode a non-2xx response into `ApiError`, reporting 5xx to crash reporting. */
async function toApiError(
  response: Response,
  correlationId: string,
  endpoint: string,
): Promise<ApiError> {
  const payload = await parseResponseBody(response);
  const apiError = new ApiError(
    `Request failed with status ${response.status}`,
    response.status,
    payload,
  );
  // Only report server errors (5xx) — 4xx are expected business logic
  // (auth, validation, not-found) and would create noise in crash reports.
  if (response.status >= 500) {
    crashReporting.recordError(apiError, {
      correlationId,
      endpoint,
      status: String(response.status),
    });
  }
  return apiError;
}

/**
 * Run `send` with every failure funnelled through crash reporting under
 * one correlation id. `ApiError` is re-thrown untouched (already reported
 * when 5xx, intentional when 4xx); anything else is reported and re-thrown.
 */
async function withCrashReporting<T>(
  endpoint: string,
  correlationId: string,
  send: () => Promise<T>,
): Promise<T> {
  try {
    return await send();
  } catch (err) {
    if (err instanceof ApiError) throw err;
    const error = err instanceof Error ? err : new Error(String(err));
    crashReporting.recordError(error, { correlationId, endpoint });
    throw error;
  }
}

/**
 * The URL without its query string or fragment, for crash-report tags: a list
 * search (`?search=<a person's name>`) must not reach a third-party service.
 */
function withoutQuery(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

export async function httpClient<TResponse>(
  url: string,
  init: RequestInit = {},
): Promise<TResponse> {
  const correlationId = uuidv4();
  const endpoint = `${(init.method ?? "GET").toUpperCase()} ${withoutQuery(url)}`;
  return withCrashReporting(endpoint, correlationId, async () => {
    const response = await fetchWithAuthRetry(url, init, correlationId);
    if (!response.ok) throw await toApiError(response, correlationId, endpoint);
    return (await parseResponseBody(response)) as TResponse;
  });
}

/**
 * Binary GET on the trusted API: same pipeline as `httpClient` (URL
 * resolution, Bearer only on the trusted origin, 401 retry, 5xx crash
 * reporting), but the body comes back as a `Blob` with the server's
 * content-type instead of being parsed as JSON/text (which would corrupt a
 * PNG). Non-2xx responses still decode the JSON error body into `ApiError`.
 */
export async function fetchBlob(url: string): Promise<Blob> {
  const correlationId = uuidv4();
  const endpoint = `GET ${withoutQuery(url)}`;
  return withCrashReporting(endpoint, correlationId, async () => {
    const response = await fetchWithAuthRetry(url, { method: "GET" }, correlationId);
    if (!response.ok) throw await toApiError(response, correlationId, endpoint);
    return response.blob();
  });
}
